import {
  AfterViewInit,
  Component,
  computed,
  inject,
  input,
  signal
} from '@angular/core'
import { Navbar } from '../../common/navbar/navbar.component'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { Modal } from 'bootstrap'
import { AbstractRider } from '../../../modal/person.modal'
import { MyDatePipe } from '../../../pipes/date.pipe'
import { AbstractUpcomingClass, Assessment } from '../../../modal/class.modal'
import { ClassService } from '../../../services/class.service'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { commonUtil } from '../../../utils/common.util'
import { AuthService } from '../../../services/auth.service'
import { ApiDisabilityOption } from '../../../modal/api.modal'

@Component({
  selector: 'app-rider-detail',
  imports: [Navbar, TranslatePipe, MyDatePipe, ReactiveFormsModule],
  templateUrl: './rider-detail.component.html',
  styleUrl: './rider-detail.component.css'
})
export class RiderDetail implements AfterViewInit {
  private classService = inject(ClassService)
  private translateService = inject(TranslateService)
  authService = inject(AuthService)
  isCoach = input(false)
  modal?: Modal
  confirmModal?: Modal
  classId?: number
  clazz?: AbstractUpcomingClass
  rider?: AbstractRider
  assessment = signal<Assessment | null>(null)
  assessmentControls = computed(() => {
    const disabled = !this.canSubmit()
    return new FormGroup({
      communication: new FormControl({
        value: this.assessment()?.communication,
        disabled
      }),
      confident: new FormControl({
        value: this.assessment()?.confident,
        disabled
      }),
      enjoyment: new FormControl({
        value: this.assessment()?.enjoyment,
        disabled
      }),
      horsemanship: new FormControl({
        value: this.assessment()?.horsemanship,
        disabled
      }),
      physicalChange: new FormControl({
        value: this.assessment()?.physicalChange,
        disabled
      }),
      remark: new FormControl({
        value: this.assessment()?.remark,
        disabled
      })
    })
  })
  isAssessmentLoading = true
  isAssessmentUpdating = false
  private disabilityOptions: ApiDisabilityOption[] = []

  constructor() {
    this.classService.getDisabilityOptions().subscribe({
      next: (options) => {
        this.disabilityOptions = [...options.physical, ...options.mental]
      },
      error: () => {}
    })
  }

  disabilityName(code: string): string {
    let option = this.disabilityOptions.find((o) => o.code === code)
    if (!option) {
      return code
    }
    switch (this.translateService.currentLang) {
      case 'zh-CN':
        return option.name_zh_cn
      case 'zh-HK':
        return option.name_zh_hk
      default:
        return option.name_en
    }
  }

  riderDisabilityLines(rider?: AbstractRider): string[] {
    if (!rider) {
      return []
    }
    let lines: string[] = []
    if (rider.physicalDisabilities) {
      lines.push(
        ...rider.physicalDisabilities
          .split(',')
          .map((d) => this.disabilityName(d.trim()))
          .filter((d) => !!d)
      )
    }
    if (rider.physicalDisabilitiesOther) {
      lines.push(rider.physicalDisabilitiesOther)
    }
    if (rider.mentalDisabilities) {
      lines.push(
        ...rider.mentalDisabilities
          .split(',')
          .map((d) => this.disabilityName(d.trim()))
          .filter((d) => !!d)
      )
    }
    if (rider.mentalDisabilitiesOther) {
      lines.push(rider.mentalDisabilitiesOther)
    }
    return lines
  }

  displayName(
    name?: {
      enName?: string
      cnName?: string
    } | null
  ): string {
    if (!name) {
      return ''
    }
    switch (this.translateService.currentLang) {
      case 'zh-CN':
      case 'zh-HK':
        return name.cnName ?? name.enName ?? ''
      default:
        return name.enName ?? name.cnName ?? ''
    }
  }

  showConfirmer() {
    return !!(
      this.assessment()?.confirmer?.name?.enName ||
      this.assessment()?.confirmer?.name?.cnName
    )
  }

  show(clazz: AbstractUpcomingClass, rider: AbstractRider) {
    this.clazz = clazz
    this.classId = clazz.id
    this.rider = rider
    this.modal?.show()
    this.getAssessment()
  }

  hide() {
    this.modal?.hide()
  }

  getAssessment() {
    if (!this.classId || !this.rider?.id) {
      return
    }
    this.isAssessmentLoading = true
    this.assessment.set(null)
    this.classService
      .getRiderAssessment(this.classId!, this.rider.id)
      .subscribe({
        next: (assessment) => {
          this.assessment.set(assessment)
          this.isAssessmentLoading = false
        },
        error: () => (this.isAssessmentLoading = false)
      })
  }

  isUpdated() {
    let { communication, confident, enjoyment, horsemanship, physicalChange } =
      this.assessment() ?? {}
    return !!(
      communication ||
      confident ||
      enjoyment ||
      horsemanship ||
      physicalChange
    )
  }

  disableUpdate() {
    if (this.canSubmit()) {
      return this.isAssessmentLoading || this.isAssessmentUpdating
    }
    return true
  }

  onConfirmClick() {
    if (this.isCoach()) {
      this.confirmModal?.show()
      return
    }
    this.updateAssessment()
  }

  canSubmit() {
    let assessment = this.assessment()
    if (assessment?.confirmer || assessment?.status === 'confirmed') {
      return false
    }
    if (!this.isCoach() && assessment?.assessor) {
      return false
    }
    return true
  }

  updateAssessment() {
    if (!this.classId || !this.rider?.id) {
      return
    }
    this.isAssessmentUpdating = true
    this.confirmModal?.hide()
    let newAssessment = this.assessmentControls().getRawValue() as Assessment
    this.classService
      .updateRiderAssessment(this.classId!, this.rider.id, newAssessment)
      .subscribe({
        next: () => {
          this.isAssessmentUpdating = false
          this.getAssessment()
          commonUtil.alert(
            this.translateService.instant(
              'app.assessment.assessmentUpdateSuccessfully'
            ),
            'success'
          )
        },
        error: () => (this.isAssessmentUpdating = false)
      })
  }

  ngAfterViewInit(): void {
    this.modal = Modal.getOrCreateInstance('#riderDetail')
    this.confirmModal = Modal.getOrCreateInstance('#confirmAssessment')
  }
}
